/**
 * Reorder in the sidebar tree: POST /hierarchy/reorder with a fractional
 * `sortKey`, neighbours from `resolveDropNeighbours`, optimistic order from
 * `reorderSiblings`.
 *
 * It wraps each row's shared canvas drag and drop props (`useItemDnd`):
 *
 * - a ONE-item drag in a row's top or bottom band (`treeDropZone`) draws an
 *   insertion line and, dropped, places the item beside the row. Beside a
 *   sibling it reorders (painted at once, put back if the request fails).
 *   Beside a row in another level it moves the item there first (one bulk
 *   move, with its undo) and then places it, as the legacy tree did; a move
 *   the folder drop target would refuse (a collection beside its own child,
 *   a read-only destination) is refused the same way, before any request.
 * - anything else (the middle of a collection, OS files, a drag of several
 *   items) goes to the row's own props untouched: hover-arm, move INTO.
 *
 * Alt+Up and Alt+Down do the same from the keyboard: the focused row swaps
 * places with its previous or next sibling of the same kind.
 */

import { useCallback, useEffect, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { moveFailureText, useBulkMove } from '../../../hooks/workspace/useBulkMove';
import { queryKeys } from '../../../lib/queryClient';
import { apiService } from '../../../services/api.service';
import {
  reorderHierarchyItem,
  type HierarchyChildrenPage,
} from '../../../services/api/hierarchy.api';
import { safeAnalytics } from '../../../utils/analytics';
import { readItemDragPayload, type DraggedItem } from '../../navigation/HierarchyTree.dnd';
import { draggingItems, leaveDropTarget, resetDropTarget } from '../../workspace/dnd/dragState';
import type { ItemDndProps } from '../../workspace/dnd/useItemDnd';
import { toDragged } from '../../workspace/dnd/useItemDrag';
import {
  levelNodes,
  parentRowOf,
  placementRefusal,
  reorderSiblings,
  resolveDropNeighbours,
  sameKindNodes,
  zoneOf,
  type ReorderPosition,
} from './treeReorder';
import type { TreeRow } from './useFolderTreeRows';

/** Where the insertion line is drawn: beside which row, above or below it. */
export interface TreeDropLine {
  readonly rowId: string;
  readonly position: ReorderPosition;
  /** The row's level, so the line starts at its indent. */
  readonly level: number;
}

type Method = 'drag' | 'keyboard';

const entityTypeOf = (dragged: DraggedItem) => (dragged.kind === 'folder' ? 'project' : 'artifact');

export function useTreeReorder(rows: ReadonlyArray<TreeRow>) {
  const workspaceId = useWorkspaceId() ?? '';
  const queryClient = useQueryClient();
  const { mutateAsync: bulkMove } = useBulkMove();
  const toast = useAppToast();
  const [dropLine, setDropLine] = useState<TreeDropLine | null>(null);

  // A drag that ends anywhere (a drop elsewhere, Escape) takes the line with it.
  useEffect(() => {
    const clear = () => setDropLine(null);
    const events = ['dragend', 'drop'] as const;
    events.forEach((type) => window.addEventListener(type, clear));
    return () => events.forEach((type) => window.removeEventListener(type, clear));
  }, []);

  const levelKey = useCallback(
    (parentId: string | null) =>
      parentId
        ? queryKeys.hierarchy.children('project', parentId)
        : queryKeys.hierarchy.root(workspaceId),
    [workspaceId],
  );

  /**
   * Where `dragged` lands beside `row`: its same-kind neighbors in that level
   * (none when there is nothing to place it against), and the request that
   * puts it there.
   */
  const requestPlacement = useCallback(
    (dragged: DraggedItem, row: TreeRow, position: ReorderPosition) => {
      const neighbors = resolveDropNeighbours({
        siblings: sameKindNodes(levelNodes(rows, row.parentId), dragged),
        targetNodeId: row.item.id,
        position,
        draggedNodeId: dragged.id,
      });
      return {
        hasNeighbor: Boolean(neighbors.afterId || neighbors.beforeId),
        send: () =>
          reorderHierarchyItem(apiService, {
            entityId: dragged.id,
            entityType: entityTypeOf(dragged),
            ...neighbors,
          }),
      };
    },
    [rows],
  );

  /** Same level: the reorder, painted at once and put back when it fails. */
  const reorder = useCallback(
    async (dragged: DraggedItem, row: TreeRow, position: ReorderPosition, method: Method) => {
      const level = levelNodes(rows, row.parentId);
      const next = reorderSiblings(level, dragged.id, row.item.id, position);
      if (next.every((node, i) => node.id === level[i]?.id)) return; // already there
      const placement = requestPlacement(dragged, row, position);
      if (!placement.hasNeighbor) return;
      const key = levelKey(row.parentId);
      const entityType = entityTypeOf(dragged);
      // Only a first page holding both rows can be reordered in place; a row
      // a "Show more" page loaded repaints when the level refetches.
      const previous = queryClient.getQueryData<HierarchyChildrenPage>(key);
      const holdsBoth = [dragged.id, row.item.id].every((id) =>
        previous?.nodes.some((n) => n.id === id),
      );
      let optimistic: HierarchyChildrenPage | undefined;
      if (previous && holdsBoth) {
        await queryClient.cancelQueries({ queryKey: key });
        // What the cache now holds (structural sharing copies), for the rollback check.
        optimistic = queryClient.setQueryData<HierarchyChildrenPage>(key, {
          ...previous,
          nodes: reorderSiblings(previous.nodes, dragged.id, row.item.id, position),
        });
      }
      try {
        const result = await placement.send();
        if (!result.success) throw new Error(result.error?.message || 'Failed to reorder');
        safeAnalytics.track(ANALYTICS_EVENTS.SIDEBAR_ITEM_REORDERED, {
          entity_type: entityType,
          method,
          surface: 'tree',
        });
      } catch (error) {
        // Put the old order back only if nothing newer replaced ours since.
        if (optimistic && queryClient.getQueryData(key) === optimistic) {
          queryClient.setQueryData(key, previous);
        }
        toast({
          title: 'Could not reorder',
          description: error instanceof Error ? error.message : undefined,
          status: 'error',
        });
      } finally {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    [rows, requestPlacement, levelKey, queryClient, toast],
  );

  /** Another level: move the item there (one bulk move, with undo), then place it. */
  const moveBeside = useCallback(
    async (dragged: DraggedItem, row: TreeRow, position: ReorderPosition) => {
      const refusal = placementRefusal(rows, dragged, row);
      if (refusal) {
        toast({ title: 'Nothing moved', description: refusal, status: 'warning' });
        return;
      }
      const parent = parentRowOf(rows, row);
      try {
        await bulkMove({
          items: [dragged],
          targetFolderId: row.parentId,
          targetName: parent?.item.name,
        });
      } catch (error) {
        toast({
          title: 'Could not move',
          description: moveFailureText(error, [dragged]),
          status: 'error',
        });
        return;
      }
      // The move has committed, so a failed placement is reported, never
      // turned into a failed move (the legacy tree's rule).
      let placed = false;
      try {
        placed = (await requestPlacement(dragged, row, position).send()).success;
      } catch {
        placed = false;
      }
      if (!placed) {
        toast({
          title: `${dragged.name} moved to the end of the list`,
          description: "It couldn't be placed where it was dropped.",
          status: 'warning',
        });
      }
      for (const parentId of new Set([dragged.parentId, row.parentId])) {
        void queryClient.invalidateQueries({ queryKey: levelKey(parentId) });
      }
    },
    [rows, bulkMove, requestPlacement, levelKey, queryClient, toast],
  );

  const place = useCallback(
    (dragged: DraggedItem, row: TreeRow, position: ReorderPosition, method: Method) =>
      dragged.parentId === row.parentId
        ? reorder(dragged, row, position, method)
        : moveBeside(dragged, row, position),
    [reorder, moveBeside],
  );

  const clearLineAt = (rowId: string) =>
    setDropLine((line) => (line?.rowId === rowId ? null : line));

  /** The row's drag and drop props, with the reorder bands layered over `base`. */
  const rowProps = (row: TreeRow, base: ItemDndProps): ItemDndProps => {
    const id = row.item.id;
    const hover = (
      event: DragEvent<HTMLElement>,
      fallback: ((event: DragEvent<HTMLElement>) => void) | undefined,
    ) => {
      const dragged = draggingItems();
      const position = zoneOf(event, row, dragged);
      if (!position) {
        clearLineAt(id);
        fallback?.(event);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // A band is never "into": the row must not arm as a folder target.
      leaveDropTarget(id);
      if (placementRefusal(rows, dragged[0], row)) {
        event.dataTransfer.dropEffect = 'none';
        clearLineAt(id);
        return;
      }
      event.dataTransfer.dropEffect = 'move';
      setDropLine((line) =>
        line?.rowId === id && line.position === position
          ? line
          : { rowId: id, position, level: row.level },
      );
    };
    return {
      ...base,
      onDragEnter: (event) => hover(event, base.onDragEnter),
      onDragOver: (event) => hover(event, base.onDragOver),
      onDragLeave: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) clearLineAt(id);
        base.onDragLeave?.(event);
      },
      onDrop: (event) => {
        // The payload, not the drag state, names what landed (it can come
        // from another window); the band is worked out again from it.
        const dragged = readItemDragPayload(event.dataTransfer);
        const position = zoneOf(event, row, dragged);
        setDropLine(null);
        if (!position) {
          base.onDrop?.(event);
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        resetDropTarget();
        void place(dragged[0], row, position, 'drag');
      },
    };
  };

  /** Alt+Up / Alt+Down on a row: swap with the previous / next sibling of its kind. */
  const onKeyDown = (event: KeyboardEvent<HTMLElement>, row: TreeRow | undefined) => {
    if (!row || !row.item.canEdit) return;
    if (!event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    if (!(event.target instanceof Element) || event.target.getAttribute('role') !== 'treeitem') {
      return;
    }
    event.preventDefault();
    const dragged = toDragged(row.item);
    const siblings = rows.filter(
      (r) => r.parentId === row.parentId && r.item.kind === row.item.kind,
    );
    const index = siblings.findIndex((r) => r.item.id === row.item.id);
    const up = event.key === 'ArrowUp';
    const neighbor = siblings[up ? index - 1 : index + 1];
    if (!neighbor) return;
    void place(dragged, neighbor, up ? 'before' : 'after', 'keyboard');
  };

  return { dropLine, rowProps, onKeyDown };
}
